/* eslint-disable @typescript-eslint/no-explicit-any */
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { get } from 'lodash';
import { arrayToTree, ArrayToTreeConfig, TreeNode } from '../utils/tree.util';

/**
 * Flat tree node interface for Material Tree
 */
export interface FlatTreeNode<T = any> extends TreeNode<T> {
  expandable: boolean;
  treeLevel: number;
}

/**
 * Tree view configuration options
 */
export interface TreeViewConfig extends Partial<ArrayToTreeConfig> {
  selectOnlyLeafNodes?: boolean;
  selectDistinctNodes?: boolean;
  suppressFirstNodeExpand?: boolean;
}

/**
 * Tree view helper class for managing tree state and operations
 * Provides utilities for tree selection, expansion, filtering, and hierarchy management
 *
 * @template T - Type of the data items
 */
export class TreeViewHelper<T = any> {
  readonly treeControl: FlatTreeControl<FlatTreeNode<T>>;

  /**
   * Primary selection model that tracks currently selected nodes in the tree.
   * This is the main source of truth for which nodes are selected.
   */
  readonly checklistSelection: SelectionModel<FlatTreeNode<T>>;

  /**
   * Backup selection model that preserves the previous selection state.
   */
  readonly currentSelectionList: SelectionModel<FlatTreeNode<T>>;

  readonly dataSource: MatTreeFlatDataSource<TreeNode<T>, FlatTreeNode<T>>;

  private readonly treeFlattener: MatTreeFlattener<TreeNode<T>, FlatTreeNode<T>>;
  private readonly config: TreeViewConfig;

  constructor(config: TreeViewConfig = {}) {
    this.config = {
      idProperty: 'id',
      parentProperty: 'parentId',
      levelProperty: 'level',
      includeAncestors: false,
      selectOnlyLeafNodes: false,
      selectDistinctNodes: false,
      suppressFirstNodeExpand: false,
      ...config
    };

    this.checklistSelection = new SelectionModel<FlatTreeNode<T>>(true);
    this.currentSelectionList = new SelectionModel<FlatTreeNode<T>>(true);

    this.treeFlattener = new MatTreeFlattener(
      this.transformer.bind(this),
      (node) => node.treeLevel,
      (node) => node.expandable,
      (node) => node.children
    );

    this.treeControl = new FlatTreeControl<FlatTreeNode<T>>(
      (node) => node.treeLevel,
      (node) => node.expandable
    );

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  /**
   * Check if a node has children
   */
  hasChild = (_: number, node: FlatTreeNode<T>): boolean => node.expandable;

  /**
   * Build tree structure from flat array
   */
  buildTreeStructure(items: T[]): TreeNode<T>[] {
    if (!items?.length) {
      return [];
    }
    return arrayToTree(items, this.config) as TreeNode<T>[];
  }

  getTreeInstanceFromItems(allItems: T[]): TreeViewHelper<T> {
    const tree: TreeViewHelper<T> = new TreeViewHelper<T>(this.config);
    const treeData = this.buildTreeStructure(allItems);
    tree.updateDataSource(treeData);
    return tree;
  }

  // Set current selection list from selected values
  setCurrentSelectionList(allItems: T[], selectedValues: T[], valueFormatter: (item: T) => any): void {
    // Build tree from all items to access all nodes
    const tree = this.getTreeInstanceFromItems(allItems);
    const selectedValuesSet = new Set(selectedValues.map((val) => valueFormatter(val)));
    // Find nodes that match the selected values
    const selectedNodes = tree?.treeControl?.dataNodes.filter((node) => selectedValuesSet.has(valueFormatter(node as T)));
    // Update current selection list
    this.currentSelectionList.clear();
    this.currentSelectionList.select(...selectedNodes);
  }

  /**
   * Build tree from filtered items, optionally including ancestors and descendants
   */
  buildTreeFromFilteredItems(filteredItems: T[], allItems: T[]): TreeNode<T>[] {
    if (!filteredItems?.length || !allItems.length) {
      return [];
    }

    const idProperty = get(this.config, 'idProperty', 'id');
    const parentProperty = get(this.config, 'parentProperty', 'parentId');
    const itemsToInclude = new Set<string | number>();

    this.collectFilteredItemIds(filteredItems, idProperty, itemsToInclude);

    if (this.config.includeAncestors) {
      this.collectAncestorIds(filteredItems, allItems, idProperty, parentProperty, itemsToInclude);
    }

    this.collectDescendantIds(filteredItems, allItems, idProperty, parentProperty, itemsToInclude);

    return this.buildTreeFromItemSet(allItems, itemsToInclude, idProperty);
  }

  /**
   * Get all descendants of a node
   */
  getDescendants(node: FlatTreeNode<T>): FlatTreeNode<T>[] {
    return this.treeControl.getDescendants(node);
  }

  /**
   * Get count of descendants for a node
   */
  getDescendantsCount(node: FlatTreeNode<T>): number {
    if (!node.expandable) {
      return 0;
    }
    return this.getDescendants(node).length;
  }

  /**
   * Check if all descendants of a node are selected
   */
  descendantsAllSelected(node: FlatTreeNode<T>): boolean {
    const descendants = this.getDescendants(node);
    if (descendants.length === 0) {
      return false;
    }

    if (this.config.selectOnlyLeafNodes) {
      const leafNodes = descendants.filter((descendant) => !descendant.expandable);
      if (leafNodes.length === 0) {
        return false;
      }
      return leafNodes.every((leaf) => this.checklistSelection.isSelected(leaf));
    }

    return descendants.every((child) => this.checklistSelection.isSelected(child));
  }

  /**
   * Check if some (but not all) descendants of a node are selected
   */
  descendantsPartiallySelected(node: FlatTreeNode<T>): boolean {
    const descendants = this.getDescendants(node);
    if (descendants.length === 0) {
      return false;
    }

    if (this.config.selectOnlyLeafNodes) {
      const leafNodes = descendants.filter((descendant) => !descendant.expandable);
      if (leafNodes.length === 0) {
        return false;
      }
      const leafSomeSelected = leafNodes.some((leaf) => this.checklistSelection.isSelected(leaf));
      const leafAllSelected = leafNodes.every((leaf) => this.checklistSelection.isSelected(leaf));
      return leafSomeSelected && !leafAllSelected;
    }

    const someSelected = descendants.some((child) => this.checklistSelection.isSelected(child));
    const allSelected = descendants.every((child) => this.checklistSelection.isSelected(child));

    return someSelected && !allSelected;
  }

  /**
   * Check if all leaf descendants are selected
   */
  leafDescendantsAllSelected(node: FlatTreeNode<T>): boolean {
    if (!node.expandable) {
      return false;
    }

    const descendants = this.getDescendants(node);
    const leafNodes = descendants.filter((descendant) => !descendant.expandable);

    if (leafNodes.length === 0) {
      return false;
    }

    return leafNodes.every((leaf) => this.checklistSelection.isSelected(leaf));
  }

  /**
   * Check if a node is selected based on configuration
   */
  isNodeSelected(node: FlatTreeNode<T>): boolean {
    if (this.config.selectOnlyLeafNodes && node.expandable) {
      return this.leafDescendantsAllSelected(node);
    }
    return this.checklistSelection.isSelected(node);
  }

  /**
   * Consolidates selections by restoring previously selected nodes that may have been temporarily hidden.
   *
   * Purpose:
   * - When the tree is filtered or rebuilt, nodes may be temporarily removed from the DOM
   * - This causes nodes to be removed from checklistSelection since they no longer exist in treeControl.dataNodes
   * - currentSelectionList acts as a memory/cache to preserve selections across filtering operations
   * - When the tree is rebuilt or filter is cleared, consolidateSelections() restores these "lost" selections
   *
   * Workflow:
   * 1. Before filtering: currentSelectionList is updated with a copy of checklistSelection
   * 2. During filtering: Some selected nodes may disappear from the tree
   * 3. After filtering: consolidateSelections() checks if any nodes from currentSelectionList are missing
   * 4. Missing nodes are re-added to checklistSelection when they reappear in the tree
   *
   * Example scenario:
   * - User selects nodes A, B, C
   * - User applies filter that hides node B
   * - currentSelectionList still has A, B, C
   * - checklistSelection only has A, C (B is not in DOM)
   * - User removes filter, B reappears
   * - consolidateSelections() detects B is back and re-selects it
   *
   * @param valueFormatter - Function to extract a comparable value from a node (typically returns node.id)
   * Used to compare nodes across different tree states since node object references change
   *
   * Important Notes:
   * - This method is called at the beginning of toggleNodeSelection() and toggleLeafSelection()
   * - It works in conjunction with buildTreeFromFilteredItems() which updates currentSelectionList
   * - The valueFormatter must return a stable identifier (like ID) that persists across tree rebuilds
   * - This ensures a smooth UX where users don't lose their selections when filtering/searching
   */
  consolidateSelections(valueFormatter: (item: T) => any): void {
    const currentSelection = this.currentSelectionList.selected;
    if (!currentSelection?.length) {
      return;
    }
    const flatNodes = this.treeControl?.dataNodes;
    const checklistSelectionSet = new Set(this.checklistSelection.selected.map((node: FlatTreeNode<T>) => valueFormatter(node as T)));
    const flatNodesSet = new Set(flatNodes.map((val) => valueFormatter(val as T)));
    currentSelection.forEach((n) => {
      const value = valueFormatter(n as T);
      if (!flatNodesSet.has(value) && !checklistSelectionSet.has(value)) {
        this.checklistSelection.select(n);
      }
    });
  }

  /**
   * Toggle selection of a node and handle children based on configuration
   */
  toggleNodeSelection(node: FlatTreeNode<T>, valueFormatter: (item: T) => any, onUpdate?: () => void): void {
    this.consolidateSelections(valueFormatter);
    this.expandNode(node);

    if (this.config.selectDistinctNodes) {
      this.checklistSelection.toggle(node);
      onUpdate?.();
      return;
    }

    if (this.config.selectOnlyLeafNodes && node.expandable) {
      this.toggleLeafDescendants(node);
      onUpdate?.();
      return;
    }

    this.checklistSelection.toggle(node);

    const descendants = this.getDescendants(node);

    if (this.checklistSelection.isSelected(node)) {
      this.checklistSelection.select(...descendants);
    } else {
      this.checklistSelection.deselect(...descendants);
    }

    this.updateAncestorsSelection(node);
    onUpdate?.();
  }

  /**
   * Toggle selection of a leaf node
   */
  toggleLeafSelection(node: FlatTreeNode<T>, valueFormatter: (item: T) => any, onUpdate?: () => void): void {
    this.consolidateSelections(valueFormatter);
    this.checklistSelection.toggle(node);

    if (!this.config.selectDistinctNodes) {
      this.updateAncestorsSelection(node);
    }

    onUpdate?.();
  }

  /**
   * Expand a node if it's expandable
   */
  expandNode(node: FlatTreeNode<T>): void {
    if (node.expandable) {
      this.treeControl.expand(node);
    }
  }

  /**
   * Expand all parent nodes of a given node
   */
  expandParentNodes(node: FlatTreeNode<T>): void {
    let parent = this.getParentNode(node);

    while (parent) {
      if (parent.expandable && !this.treeControl.isExpanded(parent)) {
        this.treeControl.expand(parent);
      }
      parent = this.getParentNode(parent);
    }
  }

  /**
   * Expand the first matched item in the tree
   */
  expandFirstMatchedItem(filteredItems: T[], valueFormatter: (item: T) => any): void {
    const flatNodes = this.treeControl?.dataNodes;
    if (!flatNodes?.length) {
      return;
    }

    let firstFilteredItem: any;

    if (!this.config.suppressFirstNodeExpand) {
      firstFilteredItem = flatNodes[0];
    }
    if (filteredItems?.length && filteredItems?.length !== flatNodes?.length) {
      firstFilteredItem = filteredItems[0];
    }

    const firstFilteredValue = String(valueFormatter(firstFilteredItem));
    const matchedNode = flatNodes.find((node) => String(valueFormatter(node as any)) === firstFilteredValue);

    if (matchedNode) {
      this.expandParentNodes(matchedNode);
      if (matchedNode.expandable) {
        this.treeControl.expand(matchedNode);
      }
    }
  }

  /**
   * Select nodes based on provided values
   */
  selectNodesFromValues(selectedValues: T[], valueFormatter: (item: T) => any): void {
    this.checklistSelection.clear();
    const flatNodes = this.treeControl?.dataNodes;
    if (!flatNodes?.length || !selectedValues.length) {
      return;
    }

    const selectedValuesSet = new Set(selectedValues.map((val) => valueFormatter(val)));
    const nodesToSelect: FlatTreeNode<T>[] = [];

    flatNodes.forEach((node) => {
      if (selectedValuesSet.has(valueFormatter(node as T))) {
        if (this.config.selectOnlyLeafNodes && node.expandable && !this.config.selectDistinctNodes) {
          return;
        }
        nodesToSelect.push(node);
        this.expandParentNodes(node);
        this.expandNode(node);
      }
    });

    if (nodesToSelect.length) {
      this.checklistSelection.select(...nodesToSelect);
      if (!this.config.selectDistinctNodes) {
        nodesToSelect.forEach((node) => {
          this.updateAncestorsSelection(node);
        });
      }
    }
  }

  /**
   * Get the parent node of a given node
   */
  getParentNode(node: FlatTreeNode<T>): FlatTreeNode<T> | null {
    const currentLevel = node.treeLevel;

    if (currentLevel < 1) {
      return null;
    }

    const dataNodes = this.treeControl?.dataNodes;
    if (!dataNodes?.length) {
      return null;
    }

    const startIndex = dataNodes.indexOf(node);
    if (startIndex < 0) {
      return null;
    }

    for (let i = startIndex - 1; i >= 0; i--) {
      const currentNode = dataNodes[i];

      if (currentNode.treeLevel < currentLevel) {
        return currentNode;
      }
    }

    return null;
  }

  /**
   * Get all selected nodes as flat array
   */
  getSelectedNodes(): FlatTreeNode<T>[] {
    return this.checklistSelection.selected;
  }

  /**
   * Clear all selections
   */
  clearSelection(): void {
    this.checklistSelection.clear();
    this.currentSelectionList.clear();
  }

  /**
   * Update the data source with new tree data
   */
  updateDataSource(treeData: TreeNode<T>[]): void {
    this.dataSource.data = treeData;
  }

  /**
   * Get configuration
   */
  getConfig(): TreeViewConfig {
    return { ...this.config };
  }

  // Private helper methods

  private transformer(node: TreeNode<T>, treeLevel: number): FlatTreeNode<T> {
    return {
      ...node,
      expandable: !!node.children?.length,
      treeLevel
    };
  }

  private toggleLeafDescendants(node: FlatTreeNode<T>): void {
    const descendants = this.getDescendants(node);
    const leafNodes = descendants.filter((descendant) => !descendant.expandable);

    if (leafNodes.length === 0) {
      return;
    }

    const allLeafsSelected = leafNodes.every((leaf) => this.checklistSelection.isSelected(leaf));

    if (allLeafsSelected) {
      this.checklistSelection.deselect(...leafNodes);
    } else {
      this.checklistSelection.select(...leafNodes);
    }
  }

  private updateAncestorsSelection(node: FlatTreeNode<T>): void {
    if (this.config.selectOnlyLeafNodes) {
      return;
    }

    let parent = this.getParentNode(node);

    while (parent) {
      const descendants = this.getDescendants(parent);

      if (descendants.length === 0) break;

      if (this.descendantsAllSelected(parent)) {
        this.checklistSelection.select(parent);
      } else {
        this.checklistSelection.deselect(parent);
      }

      parent = this.getParentNode(parent);
    }
  }

  private getProperty(item: T, prop: string) {
    return get(item, prop, undefined);
  }

  private collectFilteredItemIds(filteredItems: T[], idProperty: string, itemsToInclude: Set<string | number>): void {
    filteredItems.forEach((item) => {
      const id = this.getProperty(item, idProperty) as string;
      if (id !== undefined) {
        itemsToInclude.add(id);
      }
    });
  }

  private collectAncestorIds(filteredItems: T[], allItems: T[], idProperty: string, parentProperty: string, itemsToInclude: Set<string | number>): void {
    const itemMap = this.createItemMap(allItems, idProperty);

    filteredItems.forEach((filteredItem) => {
      let currentItem: T | undefined = filteredItem;
      while (currentItem) {
        const parentId = this.getProperty(currentItem, parentProperty) as string;
        if (!parentId || itemsToInclude.has(parentId)) break;

        itemsToInclude.add(parentId);
        currentItem = itemMap.get(parentId);
      }
    });
  }

  private collectDescendantIds(filteredItems: T[], allItems: T[], idProperty: string, parentProperty: string, itemsToInclude: Set<string | number>): void {
    const childrenMap = this.createChildrenMap(allItems, parentProperty);

    const addDescendantsRecursive = (parentId: string | number): void => {
      const children = childrenMap.get(parentId);
      if (!children) return;

      children.forEach((child) => {
        const childId = this.getProperty(child, idProperty) as string;
        if (childId !== undefined && !itemsToInclude.has(childId)) {
          itemsToInclude.add(childId);
          addDescendantsRecursive(childId);
        }
      });
    };

    filteredItems.forEach((item) => {
      const id = this.getProperty(item, idProperty) as string;
      if (id !== undefined) {
        addDescendantsRecursive(id);
      }
    });
  }

  private createItemMap(items: T[], idProperty: string): Map<string | number, T> {
    const itemMap = new Map<string | number, T>();
    items.forEach((item) => {
      const id = this.getProperty(item, idProperty) as string;
      if (id !== undefined) {
        itemMap.set(id, item);
      }
    });
    return itemMap;
  }

  private createChildrenMap(items: T[], parentProperty: string): Map<string | number, T[]> {
    const childrenMap = new Map<string | number, T[]>();
    items.forEach((item) => {
      const parentId = this.getProperty(item, parentProperty) as string;
      if (parentId !== undefined) {
        if (!childrenMap.has(parentId)) {
          childrenMap.set(parentId, []);
        }
        const children = childrenMap.get(parentId);
        if (children) {
          children.push(item);
        }
      }
    });
    return childrenMap;
  }

  private buildTreeFromItemSet(allItems: T[], itemsToInclude: Set<string | number>, idProperty: string): TreeNode<T>[] {
    const itemsForTree = allItems.filter((item) => {
      const id = this.getProperty(item, idProperty) as string;
      return id !== undefined && itemsToInclude.has(id);
    });

    return arrayToTree(itemsForTree, this.config) as TreeNode<T>[];
  }
}
